import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useMembers } from "@/context/MemberContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { VoiceMicButton } from "@/components/VoiceInput";
import { toast } from "sonner";
import { Field, Header, EmptyState } from "@/pages/Vitals";
import { Plus, Pill, Trash2, Sun, Sunrise, Sunset, Moon, ScanLine, Loader2 } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

const empty = () => ({ name: "", dosage: "", start_date: today(),
  morning: false, afternoon: false, evening: false, night: false, food_timing: "after" });

const slots = [
  { key: "morning", label: "Morning", icon: Sunrise },
  { key: "afternoon", label: "Afternoon", icon: Sun },
  { key: "evening", label: "Evening", icon: Sunset },
  { key: "night", label: "Night", icon: Moon },
];

export default function Medications() {
  const { activeId, active } = useMembers();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [extracted, setExtracted] = useState(null);

  const load = useCallback(() => {
    if (!activeId) return;
    api.get(`/medications?member_id=${activeId}`).then(r => setItems(r.data));
  }, [activeId]);
  useEffect(() => { load(); }, [load]);

  const onScan = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setScanning(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = String(reader.result).split(",")[1];
        try {
          const r = await api.post("/ocr/scan-prescription", { image_base64: base64, mime_type: file.type });
          setExtracted(r.data);
          const meds = r.data.medications || [];
          if (meds.length > 0) {
            const m = meds[0];
            setForm(f => ({ ...f, ...m, start_date: r.data.date || f.start_date }));
            toast.success(`Extracted ${meds.length} medication${meds.length>1?"s":""}. First one filled in form.`);
          } else toast.message("No medications detected");
        } catch { toast.error("Could not scan prescription"); }
        finally { setScanning(false); }
      };
      reader.readAsDataURL(file);
    } catch { setScanning(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Enter medication name"); return; }
    if (!activeId) { toast.error("Select a member first"); return; }
    setLoading(true);
    try {
      await api.post("/medications", { ...form, member_id: activeId });
      toast.success("Medication added");
      setOpen(false); setForm(empty()); setExtracted(null); load();
    } finally { setLoading(false); }
  };

  const saveAllExtracted = async () => {
    if (!extracted?.medications?.length) return;
    setLoading(true);
    try {
      for (const m of extracted.medications) {
        await api.post("/medications", {
          name: m.name, dosage: m.dosage, start_date: extracted.date || today(),
          morning: !!m.morning, afternoon: !!m.afternoon, evening: !!m.evening, night: !!m.night,
          food_timing: m.food_timing || "after", notes: m.notes, member_id: activeId,
        });
      }
      toast.success(`Saved ${extracted.medications.length} medication(s)`);
      setOpen(false); setForm(empty()); setExtracted(null); load();
    } finally { setLoading(false); }
  };

  const del = async (id) => { await api.delete(`/medications/${id}`); toast.success("Deleted"); load(); };

  return (
    <div className="space-y-6">
      <Header title="Medications" subtitle={`For ${active?.name || "—"}. Schedule by time of day and food timing. Scan prescriptions to auto-fill.`}
        action={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty()); setExtracted(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-med-btn" className="rounded-xl bg-[#4A6750] hover:bg-[#3B5340]"><Plus className="w-4 h-4 mr-2" /> Add Medication</Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl max-w-xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading">Add Medication</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="med-form">
                <label className="flex items-center gap-3 p-4 border-2 border-dashed border-[#E5E7E4] rounded-xl bg-[#F9F8F6] cursor-pointer hover:border-[#4A6750] transition-colors">
                  {scanning ? <Loader2 className="w-5 h-5 animate-spin text-[#4A6750]" /> : <ScanLine className="w-5 h-5 text-[#4A6750]" />}
                  <span className="text-sm font-medium text-[#1C2E24]">{scanning ? "Reading prescription..." : "Scan a prescription image to auto-fill"}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={onScan} disabled={scanning} data-testid="scan-rx-input" />
                </label>

                {extracted?.medications?.length > 1 && (
                  <div className="p-3 rounded-xl bg-[#E8ECE9] border border-[#4A6750]/20 text-sm">
                    <p className="text-[#1C2E24] mb-2">{extracted.medications.length} medications extracted. First is in the form below.</p>
                    <Button type="button" size="sm" onClick={saveAllExtracted} data-testid="save-all-rx-btn" className="bg-[#4A6750] hover:bg-[#3B5340] rounded-lg">Save all {extracted.medications.length}</Button>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Name"><div className="flex gap-2"><Input data-testid="med-name" value={form.name || ""} onChange={e => setForm({...form, name: e.target.value})} required className="bg-[#F9F8F6] rounded-xl flex-1" /><VoiceMicButton onText={(t) => setForm(f => ({...f, name: f.name ? `${f.name} ${t}` : t}))} /></div></Field>
                  <Field label="Dosage"><Input data-testid="med-dosage" placeholder="e.g. 500 mg" value={form.dosage || ""} onChange={e => setForm({...form, dosage: e.target.value})} /></Field>
                  <Field label="Start Date"><Input data-testid="med-start" type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} /></Field>
                  <Field label="End Date"><Input data-testid="med-end" type="date" value={form.end_date || ""} onChange={e => setForm({...form, end_date: e.target.value})} /></Field>
                </div>

                <div>
                  <p className="text-xs font-semibold tracking-[0.05em] uppercase text-[#7D8F84] mb-2">Dosage Schedule</p>
                  <div className="grid grid-cols-4 gap-2">
                    {slots.map(s => (
                      <label key={s.key} data-testid={`slot-${s.key}`} className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 cursor-pointer transition-all ${form[s.key] ? "border-[#4A6750] bg-[#E8ECE9]" : "border-[#E5E7E4] bg-white"}`}>
                        <s.icon className={`w-5 h-5 ${form[s.key] ? "text-[#4A6750]" : "text-[#7D8F84]"}`} strokeWidth={1.5} />
                        <span className="text-xs font-medium">{s.label}</span>
                        <Checkbox checked={!!form[s.key]} onCheckedChange={(v) => setForm({...form, [s.key]: !!v})} className="sr-only" />
                      </label>
                    ))}
                  </div>
                </div>

                <Field label="Food Timing">
                  <Select value={form.food_timing} onValueChange={v => setForm({...form, food_timing: v})}>
                    <SelectTrigger data-testid="med-food"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="before">Before Food</SelectItem>
                      <SelectItem value="after">After Food</SelectItem>
                      <SelectItem value="anytime">Anytime</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>

                <Field label="Notes"><Textarea data-testid="med-notes" rows={2} value={form.notes || ""} onChange={e => setForm({...form, notes: e.target.value})} /></Field>
                <Button type="submit" disabled={loading} data-testid="save-med-btn" className="w-full rounded-xl bg-[#4A6750] hover:bg-[#3B5340]">Save Medication</Button>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      {items.length === 0 ? (
        <EmptyState icon={Pill} title="No medications yet" subtitle="Add a medication or scan a prescription." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map(m => <MedCard key={m.id} med={m} onDelete={() => del(m.id)} />)}
        </div>
      )}
    </div>
  );
}

function MedCard({ med, onDelete }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get(`/medications/${med.id}/adherence?days=30`).then(r => setStats(r.data)).catch(() => {});
  }, [med.id]);

  return (
    <Card className="p-5 rounded-2xl border border-[#E5E7E4]" data-testid={`med-${med.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold tracking-[0.1em] uppercase text-[#7D8F84]">{med.start_date}{med.end_date ? ` → ${med.end_date}` : ""}</p>
          <h3 className="font-heading text-xl font-medium mt-1 truncate">{med.name}</h3>
          {med.dosage && <p className="text-sm text-[#5C6D63] mt-0.5">{med.dosage}</p>}
          <div className="flex gap-1.5 mt-3 flex-wrap">
            {slots.map(s => med[s.key] && (
              <span key={s.key} className="inline-flex items-center gap-1 bg-[#E8ECE9] text-[#4A6750] rounded-full px-2.5 py-1 text-xs font-medium">
                <s.icon className="w-3 h-3" strokeWidth={1.5} /> {s.label}
              </span>
            ))}
          </div>
          {med.food_timing && (
            <span className="inline-block mt-2 text-xs px-2.5 py-1 rounded-full bg-[#F1F3F0] text-[#5C6D63] capitalize">{med.food_timing === "anytime" ? "Anytime" : `${med.food_timing} food`}</span>
          )}
          {med.notes && <p className="mt-3 text-sm text-[#5C6D63] italic">{med.notes}</p>}
        </div>
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          <button data-testid={`del-med-${med.id}`} onClick={onDelete} className="text-[#7D8F84] hover:text-[#C76B58] p-1.5"><Trash2 className="w-4 h-4" /></button>
          {stats?.percent != null && <AdherenceRing percent={stats.percent} taken={stats.taken} scheduled={stats.scheduled} />}
        </div>
      </div>
    </Card>
  );
}

function AdherenceRing({ percent, taken, scheduled }) {
  const r = 22, c = 2 * Math.PI * r;
  const dash = (percent / 100) * c;
  const color = percent >= 80 ? "#5B8266" : percent >= 50 ? "#D8A24A" : "#C76B58";
  return (
    <div className="text-center" data-testid="adherence-ring">
      <div className="relative w-14 h-14">
        <svg className="w-14 h-14 -rotate-90" viewBox="0 0 52 52">
          <circle cx="26" cy="26" r={r} stroke="#E5E7E4" strokeWidth="4" fill="none" />
          <circle cx="26" cy="26" r={r} stroke={color} strokeWidth="4" fill="none"
                  strokeLinecap="round" strokeDasharray={`${dash} ${c}`} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center text-[11px] font-semibold text-[#1C2E24]">{percent}%</div>
      </div>
      <p className="text-[10px] text-[#7D8F84] mt-1 whitespace-nowrap">{taken}/{scheduled} · 30d</p>
    </div>
  );
}
